import { blankIntake } from './personalization'
import { createBlomitPersistence, type StorageLike } from './persistence'

export const PREVIOUS_ORIGIN = 'https://bloomit.pages.dev'
export const CURRENT_ORIGIN = 'https://blomit.pages.dev'
const keys = ['bloom-it-intake', 'bloom-it-plan', 'bloom-it-remote-plan', 'bloom-it-completed-lessons'] as const
type TransferKey = typeof keys[number]
type TransferData = Record<TransferKey, string | null>

function parseData(value: unknown): TransferData {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('The previous site returned an invalid saved path.')
  const data = value as Record<string, unknown>
  const result = {} as TransferData
  for (const key of keys) {
    const raw = data[key] ?? null
    if (raw !== null && (typeof raw !== 'string' || raw.length > 200_000)) throw new Error('The saved path could not be read.')
    result[key] = raw as string | null
  }
  return result
}

/** Import only known, validated app data; never overwrite another path on the new domain. */
export function importSavedPath(value: unknown, storage: Required<StorageLike>): string {
  const data = parseData(value)
  const incoming = createBlomitPersistence({ storage: {
    getItem: (key) => data[key as TransferKey] ?? null,
    setItem: () => {},
  } })
  const intake = incoming.intake.read()
  const plan = incoming.plan.read()
  if (incoming.intake.getLastError() || incoming.plan.getLastError()) throw new Error('The previous site has an unreadable saved path.')

  let ref: { planId: string; accessToken: string } | null = null
  let completed: string[] = []
  try {
    if (data['bloom-it-remote-plan']) {
      const parsed = JSON.parse(data['bloom-it-remote-plan'])
      if (!parsed || typeof parsed.planId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parsed.planId)
        || typeof parsed.accessToken !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(parsed.accessToken)) throw new Error()
      ref = { planId: parsed.planId, accessToken: parsed.accessToken }
    }
    if (data['bloom-it-completed-lessons']) {
      const parsed = JSON.parse(data['bloom-it-completed-lessons'])
      if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string' && /^[a-z0-9-]{1,100}$/.test(id))) throw new Error()
      completed = [...new Set(parsed)]
    }
  } catch {
    throw new Error('The saved plan reference or progress could not be read.')
  }
  const hasAnswers = JSON.stringify(intake) !== JSON.stringify(blankIntake())
  if (!plan && !ref && !hasAnswers && !completed.length) throw new Error('No saved path was found. Use the browser where you previously created it, or start a new path.')

  const existing = createBlomitPersistence({ storage })
  if (existing.plan.read() || storage.getItem('bloom-it-remote-plan')
    || JSON.stringify(existing.intake.read()) !== JSON.stringify(blankIntake())
    || (storage.getItem('bloom-it-completed-lessons') && storage.getItem('bloom-it-completed-lessons') !== '[]')) {
    throw new Error('This address already has saved answers or progress. Open your current path rather than replacing it.')
  }

  const previous = keys.map((key) => storage.getItem(key))
  const next: TransferData = {
    'bloom-it-intake': JSON.stringify({ version: 1, data: intake }),
    'bloom-it-plan': plan ? JSON.stringify({ version: 1, data: plan }) : null,
    'bloom-it-remote-plan': ref ? JSON.stringify(ref) : null,
    'bloom-it-completed-lessons': JSON.stringify(completed),
  }
  try {
    for (const key of keys) {
      const raw = next[key]
      if (raw === null) storage.removeItem(key)
      else storage.setItem(key, raw)
    }
  } catch {
    for (const [index, key] of keys.entries()) {
      try {
        const raw = previous[index]
        if (raw === null) storage.removeItem(key)
        else storage.setItem(key, raw)
      } catch { /* The original domain still retains its saved data. */ }
    }
    throw new Error('This browser could not save the transferred path. Your original path is still available at the previous address.')
  }
  return plan || ref ? '/plan' : hasAnswers ? '/personalize' : '/course'
}
